import type {
  Announcement,
  AnnouncementAction,
  Checklist,
  InitOptions,
  ResourceCenterConfig,
  Traits,
  EventProperties,
  Tour,
  TourAction,
  Theme,
} from './types';
import { DEFAULT_THEME } from './types';
import {
  pickTour,
  startTour,
  isTourShown,
  applyTheme,
  forgetShownTour,
  notifyTrackEvent,
} from './runtime';
import { flushPendingCompletion } from './animations';
import {
  applyChecklistTheme,
  bootChecklist,
  onRouteChange as onChecklistRouteChange,
  onTourComplete as onChecklistTourComplete,
  onTrackEvent as onChecklistTrackEvent,
  openChecklistById,
} from './checklist';
import {
  applyAnnouncementTheme,
  bootAnnouncements,
  onAnnouncementRouteChange,
  onAnnouncementTraitsChange,
  openAnnouncementById,
} from './announcement';
import { applyResourceCenterTheme, bootResourceCenter } from './resource-center';
import { bootPicker } from './picker';
import { bootPreview } from './preview';

const DEFAULT_API = 'https://api.steppr.io';
const ANON_COOKIE = 'stp_aid';

type State = {
  projectKey: string;
  apiUrl: string;
  anonId: string;
  externalId: string | null;
  traits: Traits;
  tours: Tour[];
  checklists: Checklist[];
  announcements: Announcement[];
  resourceCenter: ResourceCenterConfig | null;
  theme: Theme;
  initialized: boolean;
};

const state: State = {
  projectKey: '',
  apiUrl: DEFAULT_API,
  anonId: '',
  externalId: null,
  traits: {},
  tours: [],
  checklists: [],
  announcements: [],
  resourceCenter: null,
  theme: { ...DEFAULT_THEME },
  initialized: false,
};

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]!) : null;
}

function writeCookie(name: string, value: string, days = 365) {
  const max = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${max}; SameSite=Lax`;
}

function generateAnonId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'a_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function ensureAnonId(): string {
  let id = readCookie(ANON_COOKIE);
  if (!id) {
    id = generateAnonId();
    writeCookie(ANON_COOKIE, id);
  }
  return id;
}

type SessionResponse = {
  ok: boolean;
  endUserId?: string;
  tours?: Tour[];
  checklists?: Checklist[];
  announcements?: Announcement[];
  resourceCenter?: ResourceCenterConfig | null;
  theme?: Theme;
};

function ingestTheme(res: SessionResponse | null) {
  if (res?.theme) {
    state.theme = { ...DEFAULT_THEME, ...res.theme };
    applyTheme(state.theme);
    applyChecklistTheme(state.theme);
    applyAnnouncementTheme(state.theme);
    applyResourceCenterTheme(state.theme);
  }
  // After theme is applied, fire any deferred completion animation that
  // was scheduled on the previous page (multi-page tours that completed
  // via an anchor-click on the last step). No-op if there's nothing
  // pending or the marker has aged past its TTL.
  flushPendingCompletion(state.theme.primaryColor);
}

function ingestChecklists(res: SessionResponse | null) {
  if (!res?.checklists) return;
  state.checklists = res.checklists;
  bootChecklist(
    state.checklists,
    checklistCallbacks,
    state.theme,
    location.href,
    state.traits,
  );
}

function ingestAnnouncements(res: SessionResponse | null) {
  if (!res?.announcements) return;
  state.announcements = res.announcements;
  bootAnnouncements(
    state.announcements,
    announcementCallbacks,
    state.theme,
    location.href,
    state.traits,
  );
}

// The resource center boots on every ingest because its content (tour /
// checklist / announcement lists) may have changed even if the config didn't.
// `bootResourceCenter` is idempotent - it tears down when the config is
// missing or disabled, and re-renders when content changes.
function ingestResourceCenter(res: SessionResponse | null) {
  if (res?.resourceCenter !== undefined) {
    state.resourceCenter = res.resourceCenter ?? null;
  }
  bootResourceCenter({
    config: state.resourceCenter,
    callbacks: resourceCenterCallbacks,
    theme: state.theme,
    tours: state.tours,
    checklists: state.checklists,
    announcements: state.announcements,
  });
}

async function post<T = unknown>(path: string, body: unknown): Promise<T | null> {
  try {
    const res = await fetch(state.apiUrl + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Steppr-Key': state.projectKey,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors',
      keepalive: true,
    });
    if (!res.ok) return null;
    return (await res.json().catch(() => null)) as T | null;
  } catch {
    // Swallow - never break the host site due to Steppr.
    return null;
  }
}

// Where we stash edit-mode state so it survives in-page navigation that
// drops our query params. sessionStorage is per-window: when the picker
// popup closes the marker dies with it, so the persistence is naturally
// scoped to one editing session. localStorage would be too sticky.
const EDIT_SESSION_KEY = '__steppr_edit_session';

function isEditMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('steppr_edit') === 'true') {
      // First boot for this editing session: persist the opener origin so
      // the next page (after the customer's app navigates and likely
      // strips our query params) can still postMessage back to the
      // dashboard. Failing to write - e.g. when sessionStorage is
      // disabled in cookieless contexts - leaves edit mode working on
      // this page only, which is no worse than the previous behaviour.
      const opener = params.get('steppr_opener') ?? '';
      try {
        sessionStorage.setItem(EDIT_SESSION_KEY, JSON.stringify({ opener }));
      } catch {
        /* ignore */
      }
      return true;
    }
    // Subsequent loads in the same window: the dashboard launched us into
    // edit mode but the customer's router rewrote the URL and our query
    // param is gone. Rehydrate from sessionStorage so the picker stays
    // active across navigation.
    try {
      if (sessionStorage.getItem(EDIT_SESSION_KEY) !== null) return true;
    } catch {
      /* ignore */
    }
    return false;
  } catch {
    return false;
  }
}

function readPreviewParams(): { tourId: string; token: string } | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const tourId = params.get('steppr_preview');
    const token = params.get('steppr_preview_token');
    if (!tourId || !token) return null;
    return { tourId, token };
  } catch {
    return null;
  }
}


function patchHistory(): void {
  if (typeof history === 'undefined') return;
  const fire = () => window.dispatchEvent(new Event('steppr:route'));
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args: Parameters<typeof origPush>) {
    const r = origPush.apply(this, args);
    fire();
    return r;
  };
  history.replaceState = function (...args: Parameters<typeof origReplace>) {
    const r = origReplace.apply(this, args);
    fire();
    return r;
  };
  window.addEventListener('popstate', fire);
  window.addEventListener('steppr:route', () => {
    evaluate('route_change');
    onChecklistRouteChange(window.location.href);
    onAnnouncementRouteChange(window.location.href);
    // Tours that complete via anchor-click on the last step often navigate
    // via history.pushState (SPA route) rather than a full reload - no new
    // SDK boot, ingestTheme isn't re-run. Fire any deferred animation
    // here too. flushPendingCompletion clears the marker, so it can't
    // double-fire if a full reload also happens.
    flushPendingCompletion(state.theme.primaryColor);
  });
}

const runtimeCallbacks = {
  sendTourEvent: (action: TourAction, tourId: string, stepId?: string) => {
    void post('/v1/tour-event', {
      anonId: state.anonId,
      externalId: state.externalId,
      tourId,
      stepId: stepId ?? null,
      action,
    });
    // Auto-tick checklist items pointing at this tour. Fires on the
    // 'complete' lifecycle event only - partial step progress doesn't
    // count as completion.
    if (action === 'complete') onChecklistTourComplete(tourId);
  },
  // Tour-chain hook: when a tour completes with a `nextTourId` set in its
  // snapshot, look up that tour in the cache and start it after a short
  // beat so the completion animation has time to play. The chained tour
  // still has to satisfy its own targeting + triggers + frequency rules,
  // so we run it through the normal pickTour() path rather than starting
  // it unconditionally.
  onTourComplete: (_completedTourId: string, nextTourId: string | null) => {
    if (!nextTourId) return;
    const next = state.tours.find((t) => t.id === nextTourId);
    if (!next) return;
    // Re-evaluate after a tick: the closing tour's teardown plus the
    // optional confetti animation needs the event loop free.
    setTimeout(() => {
      // Skip if the user has already dismissed/completed the chained tour
      // - chains should be additive, not pestering. Targeting still
      // applies via runtime.startTour's internal checks.
      if (next.state?.dismissed || next.state?.completed) return;
      startTour(next, runtimeCallbacks);
    }, 600);
  },
};

const checklistCallbacks = {
  reportComplete: (
    checklistId: string,
    itemId: string,
    source: 'auto' | 'manual',
  ) => {
    void post('/v1/checklist/complete', {
      anonId: state.anonId,
      externalId: state.externalId,
      checklistId,
      itemId,
      source,
    });
  },
  reportDismiss: (checklistId: string) => {
    void post('/v1/checklist/dismiss', {
      anonId: state.anonId,
      externalId: state.externalId,
      checklistId,
    });
  },
  reportView: (checklistId: string) => {
    void post('/v1/checklist/view', {
      anonId: state.anonId,
      externalId: state.externalId,
      checklistId,
    });
  },
  launchTour: (tourId: string) => {
    // Look up the tour in the cached snapshot and start it via the runtime.
    // If the tour isn't in the cache (e.g., the customer linked it before
    // publishing it, or it was unpublished after the checklist published),
    // we silently no-op rather than 404 in the user's face.
    const tour = state.tours.find((t) => t.id === tourId);
    if (!tour) return;
    if (isTourShown()) return;
    startTour(tour, runtimeCallbacks);
  },
};

const announcementCallbacks = {
  reportEvent: (announcementId: string, action: AnnouncementAction) => {
    void post('/v1/announcement-event', {
      anonId: state.anonId,
      externalId: state.externalId,
      announcementId,
      action,
    });
  },
};

const resourceCenterCallbacks = {
  launchTour: (tourId: string) => {
    const tour = state.tours.find((t) => t.id === tourId);
    if (!tour) return;
    if (isTourShown()) return;
    // Replay-from-RC: drop the dismissed/completed state so the tour starts
    // from step 0 and the runtime doesn't bail in pickTour. This is a local
    // override only - server-side state stays as it was. The visitor can
    // dismiss again to restore the suppression.
    const replayable: typeof tour = {
      ...tour,
      state: tour.state
        ? { ...tour.state, dismissed: false, completed: false, inProgressStepIndex: null }
        : undefined,
    };
    forgetShownTour(tour.id);
    startTour(replayable, runtimeCallbacks);
  },
  openChecklist: (checklistId: string) => {
    openChecklistById(checklistId);
  },
  openAnnouncement: (announcementId: string) => {
    openAnnouncementById(announcementId);
  },
  // Telemetry - routed through /v1/track so existing analytics + outbound
  // webhook plumbing pick them up without a new endpoint.
  reportOpen: () => {
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name: 'resource_center.opened',
      properties: {},
    });
  },
  reportLinkClick: (linkId: string) => {
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name: 'resource_center.link_clicked',
      properties: { linkId },
    });
  },
  reportTourLaunched: (tourId: string) => {
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name: 'resource_center.tour_launched',
      properties: { tourId },
    });
  },
  reportChecklistOpened: (checklistId: string) => {
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name: 'resource_center.checklist_opened',
      properties: { checklistId },
    });
  },
  reportAnnouncementOpened: (announcementId: string) => {
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name: 'resource_center.announcement_opened',
      properties: { announcementId },
    });
  },
};

function evaluate(
  kind: 'page_load' | 'route_change' | 'event',
  eventName?: string,
): void {
  if (isTourShown()) return; // already running a tour; don't start another
  if (state.tours.length === 0) return;
  const url = window.location.href;
  const candidate = pickTour(state.tours, { url, kind, eventName }, state.traits);
  if (candidate) startTour(candidate, runtimeCallbacks);
}

export const Steppr = {
  init(options: InitOptions): void {
    if (state.initialized) return;
    if (!options.projectKey) {
      // eslint-disable-next-line no-console
      console.warn('[Steppr] init called without projectKey');
      return;
    }
    state.projectKey = options.projectKey;
    state.apiUrl = options.apiUrl ?? DEFAULT_API;
    state.anonId = ensureAnonId();
    state.initialized = true;

    if (!options.disableRouteTracking) patchHistory();

    if (isEditMode()) {
      // Lite edit mode: boot the element picker. No tour fetch, no runtime.
      bootPicker();
      return;
    }

    const preview = readPreviewParams();
    if (preview) {
      // Preview mode: fetch the draft snapshot, render with a subtle toolbar,
      // bypass targeting/triggers. No MAU, no analytics.
      bootPreview({
        apiUrl: state.apiUrl,
        projectKey: state.projectKey,
        tourId: preview.tourId,
        token: preview.token,
      });
      return;
    }

    void (async () => {
      const res = await post<SessionResponse>('/v1/session', {
        anonId: state.anonId,
        url: location.href,
      });
      state.tours = res?.tours ?? [];
      ingestTheme(res);
      ingestChecklists(res);
      ingestAnnouncements(res);
      ingestResourceCenter(res);
      evaluate('page_load');
    })();
  },

  identify(externalId: string, traits?: Traits): void {
    if (!state.initialized) return;
    state.externalId = externalId;
    if (traits) state.traits = { ...state.traits, ...traits };
    void (async () => {
      const res = await post<SessionResponse>('/v1/identify', {
        anonId: state.anonId,
        externalId,
        traits: state.traits,
      });
      if (res?.tours) state.tours = res.tours;
      ingestTheme(res);
      ingestChecklists(res);
      ingestAnnouncements(res);
      ingestResourceCenter(res);
      // New identity / traits may unlock tours - re-evaluate.
      evaluate('page_load');
      // New traits might also flip targeting on a not-yet-shown announcement.
      onAnnouncementTraitsChange(location.href, state.traits);
    })();
  },

  track(name: string, properties?: EventProperties): void {
    if (!state.initialized) return;
    void post('/v1/track', {
      anonId: state.anonId,
      externalId: state.externalId,
      name,
      properties: properties ?? {},
    });
    // Event triggers fire synchronously against the cached tour list - no need
    // to wait for the track round-trip.
    evaluate('event', name);
    // Satisfy any in-progress step that's gated on this event name. Same
    // synchronous path - the user expects the Next button to enable the
    // moment the track call returns.
    notifyTrackEvent(name);
    // Auto-tick any 'event' checklist items waiting on this name. Local
    // optimistic update; persisted via /v1/checklist/complete inside the
    // checklist module.
    onChecklistTrackEvent(name);
  },

  reset(): void {
    state.externalId = null;
    state.traits = {};
    // Leave anonId alone - stable visitor identity survives logout.
  },

  /**
   * Programmatically start a tour by id, ignoring its targeting + triggers.
   * Use this to wire tours to custom UI affordances ("?, take a tour" buttons,
   * onboarding nudges from your own state machine, etc.).
   *
   * Returns `true` if the tour was found in the published cache and a start
   * was attempted, `false` if not (no published snapshot, wrong id, runtime
   * busy with another tour). The runtime's own "only one tour at a time"
   * guard still applies, so calling this while another tour is open silently
   * no-ops.
   *
   * Targeting and triggers are intentionally bypassed - that's the point of
   * the API. Per-end-user dismiss/complete state is also ignored, so this
   * can re-launch a tour the user previously closed.
   */
  startTour(tourId: string): boolean {
    if (!state.initialized) return false;
    const tour = state.tours.find((t) => t.id === tourId);
    if (!tour) return false;
    startTour(tour, runtimeCallbacks);
    return true;
  },
};

export type StepprClient = typeof Steppr;
